import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { PdpaLanguage, PdpaNotice as Notice } from '@sdg/shared'
import { Card, CardContent } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { cn } from '../lib/cn'
import { get } from '../lib/api'

/**
 * The PDPA notice — slide 30's "PDPA 2010 consent notice".
 *
 * Public and outside every guard: a student has to be able to read this
 * *before* handing over any details, so putting it behind a session would
 * defeat the point. Agreeing to a notice you cannot read is not informed
 * consent, which is what the registration form previously asked for.
 *
 * Both languages are offered because section 7(3) of the Act requires the
 * notice in the national language as well as English.
 */

interface PdpaResponse {
  version: string
  contact: { controller: string; department: string; email: string }
  notice: Record<PdpaLanguage, Notice>
}

const LANGUAGES: { key: PdpaLanguage; label: string }[] = [
  { key: 'en', label: 'English' },
  { key: 'ms', label: 'Bahasa Malaysia' },
]

export default function PdpaNotice() {
  const [language, setLanguage] = useState<PdpaLanguage>('en')

  const query = useQuery({
    queryKey: ['pdpa'],
    // 'none': readable before a student has any session at all.
    queryFn: () => get<PdpaResponse>('/api/pdpa', 'none'),
    staleTime: Infinity,
  })

  if (query.isLoading) {
    return (
      <Card>
        <CardContent>
          <p className="py-10 text-center text-sm text-slate-500">Loading the notice…</p>
        </CardContent>
      </Card>
    )
  }

  if (query.isError || !query.data) {
    return (
      <Card>
        <CardContent className="text-center">
          <p className="text-sm text-slate-600">
            The notice could not be loaded. It is also available from{' '}
            <span className="font-semibold">registrar@lincoln.edu.my</span> on request.
          </p>
          <Button variant="outline" block className="mt-4" asChild>
            <Link to="/">Back to registration</Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  const notice = query.data.notice[language]

  return (
    <Card>
      <CardContent className="space-y-5">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:underline"
        >
          <ArrowLeft className="size-3.5" strokeWidth={2.4} />
          Back to registration
        </Link>

        <header>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
            <ShieldCheck className="size-3.5" strokeWidth={2.4} />
            PDPA 2010
          </span>
          <h1 className="mt-2.5 text-xl font-bold leading-tight text-[#0B1E3F]">{notice.title}</h1>
          <p className="mt-1.5 text-sm leading-snug text-slate-600">{notice.intro}</p>
        </header>

        {/* Both languages, because section 7(3) requires both. */}
        <div
          role="group"
          aria-label="Notice language"
          className="flex gap-1.5 rounded-xl bg-slate-100 p-1"
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.key}
              type="button"
              onClick={() => setLanguage(l.key)}
              aria-pressed={language === l.key}
              className={cn(
                'flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
                language === l.key
                  ? 'bg-white text-[#0B1E3F] shadow-sm'
                  : 'text-slate-500 hover:text-slate-700',
              )}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {notice.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="text-sm font-bold text-[#0B1E3F]">{section.heading}</h2>
              <ul className="mt-1.5 space-y-1.5">
                {section.body.map((line, i) => (
                  <li
                    key={i}
                    className="flex gap-2 text-[13px] leading-snug text-slate-600"
                  >
                    <span aria-hidden className="mt-1.5 size-1 shrink-0 rounded-full bg-slate-300" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <footer className="space-y-3 border-t border-slate-100 pt-4">
          <p className="rounded-xl bg-slate-50 px-3.5 py-3 text-[13px] leading-snug text-slate-600">
            {notice.consentStatement}
          </p>
          <p className="text-xs text-slate-400">
            Version {query.data.version} · {query.data.contact.department},{' '}
            {query.data.contact.controller} ·{' '}
            <a
              href={`mailto:${query.data.contact.email}`}
              className="font-semibold text-orange-600 hover:underline"
            >
              {query.data.contact.email}
            </a>
          </p>
        </footer>

        <Button block asChild>
          <Link to="/">Back to registration</Link>
        </Button>
      </CardContent>
    </Card>
  )
}
